<template>
  <ion-page>
    <div
      v-if="!navigating"
      class="px-[24px] bg-[#F6F4F0] fixed inset-0 z-[9999] min-h-screen flex flex-col"
    >
      <BackButton class="w-full px-[20px] mb-[16px]" :customBack="back" />
      <div class="mb-[40px]">
        <h1 class="text-[2rem] leading-[2.5rem] tracking-[-1px] font-[900] text-[#433D31] mb-[8px]">
          Forgot PIN
        </h1>
        <p class="text-[1rem] leading-[1.5rem] tracking-[-0.5px] text-[#433D31] font-[600]">
          Enter the 6-digit code we sent to<br />
          <span class="font-[900]">{{ displayEmail }}</span>
        </p>
      </div>

      <div class="flex-1">
        <div class="mb-[32px]">
          <PinInput
            :key="inputKey"
            ref="otpInput"
            v-model="otp"
            :length="6"
            :disabled="loading"
            :has-error="!!errorMsg"
            :auto-submit="true"
            :input-class="otpInputClass"
            gap-class="gap-x-[8px]"
            @complete="handleOtpComplete"
          />
        </div>

        <p v-if="errorMsg" class="text-center text-[0.875rem] text-red-500 mb-[16px]" role="alert">
          {{ errorMsg }}
        </p>

        <div class="text-center">
          <button
            @click="handleResendOtp"
            class="text-[0.875rem] text-[#246BFD] font-medium underline disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            :disabled="!canResend"
          >
            {{ resendButtonText }}
          </button>
        </div>
      </div>
    </div>
  </ion-page>
</template>

<script>
import { IonPage } from '@ionic/vue';
import PinInput from '@/components/MyGiving/Common/PinInput.vue';
import { mapState } from 'vuex';
import BackButton from '@/components/common/BackButton.vue';

export default {
  name: 'ForgotPinOtpPage',

  components: {
    IonPage,
    PinInput,
    BackButton,
  },

  data() {
    return {
      otp: '',
      errorMsg: '',
      loading: false,
      countdown: 60,
      timer: null,
      inputKey: 0,
      navigating: false,
    };
  },

  computed: {
    ...mapState('myGiving', ['resetEmail', 'resetMaskedEmail']),

    email() {
      return this.$route.query.email || this.resetEmail;
    },

    displayEmail() {
      return this.$route.query.maskedEmail || this.resetMaskedEmail || this.email;
    },

    isComplete() {
      return this.otp.length === 6;
    },

    canResend() {
      return this.countdown <= 0 && !this.loading;
    },

    resendButtonText() {
      return this.countdown > 0 ? `Resend Code in ${this.countdown}s` : 'Resend Code';
    },

    otpInputClass() {
      return [
        'h-[56px] w-full text-center text-[1.5rem] font-semibold rounded-[12px]',
        'border-2',
        this.errorMsg ? 'border-red-500' : 'border-[#D1CEC9]',
        'bg-white text-[#2B2926] outline-none transition',
        'focus:border-[#246BFD] focus:ring-2 focus:ring-[#246BFD] focus:ring-offset-2 focus:ring-offset-[#F6F4F0]',
      ].join(' ');
    },
  },

  mounted() {
    if (!this.email) {
      console.error('No email found for OTP verification');
      this.$router.replace('/my-giving/pin/forgot-pin');
      return;
    }

    this.startCountdown();
    this.$nextTick(() => this.$refs.otpInput?.focusFirst?.());
  },

  beforeUnmount() {
    this.clearCountdown();
  },

  methods: {
    handleOtpComplete(val) {
      this.otp = val;
      this.verifyOtp();
    },

    async verifyOtp() {
      if (!this.isComplete || this.loading) return;

      this.loading = true;
      this.errorMsg = '';

      try {
        const res = await this.$store.dispatch('myGiving/resetPinVerifyOtp', {
          email: this.email,
          otp: this.otp,
        });

        const data = res?.data || res;

        if (data?.state === 'reset_verified') {
          this.navigating = true;
          await this.$nextTick();

          this.$router.replace('/my-giving/pin/forgot-pin/success');
        } else if (data?.state === 'otp_invalid') {
          this.errorMsg = 'Invalid OTP. Please try again.';
          this.resetOtpInput();
        } else if (data?.state === 'otp_expired') {
          this.errorMsg = 'OTP has expired. Please request a new code.';
          this.resetOtpInput();
        } else {
          this.errorMsg = 'Invalid OTP. Please try again.';
          this.resetOtpInput();
        }
      } catch (error) {
        if (error.response?.status === 400) {
          this.errorMsg = 'Invalid OTP. Please try again.';
        } else if (error.response?.status === 429) {
          this.errorMsg = 'Too many attempts. Please try again later.';
        } else {
          this.errorMsg = 'Something went wrong. Please try again.';
        }

        this.resetOtpInput();
      } finally {
        this.loading = false;
      }
    },

    async handleResendOtp() {
      if (!this.canResend) return;

      this.loading = true;
      this.errorMsg = '';

      try {
        const res = await this.$store.dispatch('myGiving/resetPinInit', {
          email: this.email,
        });

        const data = res?.data || res;

        if (data?.state === 'reset_otp') {
          this.countdown = 60;
          this.startCountdown();
          this.resetOtpInput();
        } else {
          this.errorMsg = 'Failed to resend code. Please try again.';
        }
      } catch {
        this.errorMsg = 'Failed to resend code. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    startCountdown() {
      this.clearCountdown();

      this.timer = setInterval(() => {
        this.countdown--;

        if (this.countdown <= 0) {
          this.clearCountdown();
        }
      }, 1000);
    },

    clearCountdown() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },

    resetOtpInput() {
      this.otp = '';
      this.errorMsg = '';
      this.inputKey++;
      this.$nextTick(() => this.$refs.otpInput?.focusFirst?.());
    },

    back() {
      this.clearCountdown();

      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.replace('/my-giving/pin/forgot-pin');
      }
    },
  },
};
</script>
